﻿import 'styles/main.scss';

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import {
  unstable_HistoryRouter as HistoryRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from 'react-query';

import { sentryDsn, sentryEnvironment } from 'config/config';
import queryClient from 'config/react-query';
import BranchProvider from 'components/BranchProvider';

import history from 'global-history';

import('intersection-observer'); // eslint-disable-line @babel/no-unused-expressions

import Routes from './routes';
import { store } from './store';

// Robust findDOMNode polyfill with Fiber traversal for React 19 class components
const findDOMNodePolyfill = (instance) => {
  if (!instance) return null;
  if (instance instanceof Element) return instance;
  if (instance.current instanceof Element) return instance.current;

  let fiber = instance._reactInternals || instance._reactInternalFiber;
  while (fiber) {
    if (fiber.stateNode instanceof Element) {
      return fiber.stateNode;
    }
    fiber = fiber.child;
  }
  return null;
};

if (ReactDOM) {
  if (!ReactDOM.findDOMNode) ReactDOM.findDOMNode = findDOMNodePolyfill;
  if (ReactDOM.default && !ReactDOM.default.findDOMNode) {
    ReactDOM.default.findDOMNode = findDOMNodePolyfill;
  }
}

if (typeof window !== 'undefined') {
  window.ReactDOM = window.ReactDOM || ReactDOM;
  if (window.ReactDOM && !window.ReactDOM.findDOMNode) {
    window.ReactDOM.findDOMNode = findDOMNodePolyfill;
  }
}

const component = (
  <HistoryRouter
    history={history}
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
    <Routes />
  </HistoryRouter>
);

Sentry.init({
  dsn: sentryDsn,
  integrations: [
    // React Router v6 instrumentation. The previous config passed the v4 one to a v6
    // router, so navigations were never recorded - only the initial page load could
    // have been, and tracesSampleRate was zero, so nothing was.
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  // Full sampling on production, none anywhere else - which is what the previous
  // `? 0.0 : 0.0` was evidently reaching for before it collapsed. The asymmetry is
  // deliberate on both sides. Production carries roughly twenty active users a week, so
  // 1.0 is cheap; and a rate below 1.0 cannot answer the question this exists for -
  // "did ANYONE reach this page" - because one visit is precisely what a sample drops.
  // Staging stays at zero because the e2e suite would swamp the data with synthetic
  // page loads, and because its API runs a single worker, so timings measured there do
  // not transfer to production anyway.
  tracesSampleRate: sentryEnvironment === 'production' ? 1.0 : 0.0,
  release: `cc-ui@${process.env.APP_VERSION}`,
  environment: sentryEnvironment,
});

const root = createRoot(document.getElementById('index'));
root.render(
  <QueryClientProvider client={queryClient}>
    <Provider store={store}>
      <BranchProvider>
        {component}
      </BranchProvider>
    </Provider>
  </QueryClientProvider>
);

// expose store when run in Cypress
if (window.Cypress) {
  window.store = store;
}
